import React from "react";
import { Eye, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CostService } from "../../types/costService";
import ActionMenu, { ActionMenuItem } from "../UI/ActionMenu";

interface CostServiceTableProps {
  services: CostService[];
  selectedIds: string[];
  onSelectAll: (checked: boolean) => void;
  onSelectOne: (id: string, checked: boolean) => void;
  onEdit: (service: CostService) => void;
  onDelete: (id: string) => void;
  loading: boolean;
}

export const CostServiceTable: React.FC<CostServiceTableProps> = ({
  services,
  onEdit,
  onDelete,
  loading,
}) => {
  const { t } = useTranslation(["costservices", "common"]);

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-12 bg-gray-100 border-b border-gray-200"></div>
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="h-16 bg-gray-50 border-b border-gray-100"
          ></div>
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto max-h-[50svh] md:max-h-[55vh] lg:max-h-[60vh] xl:max-h-[70vh] overflow-y-scroll border-b border-b-gray-300">
      <table className="min-w-full border-collapse border border-gray-300">
        <thead className="bg-gray-50 text-nowrap sticky top-0 z-1">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("costservices:table.id")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("costservices:table.serviceCode")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("costservices:table.client")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("costservices:table.description")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("costservices:table.unitType")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("costservices:table.price")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("costservices:table.tax")}
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200">
              {t("common:labels.actions")}
            </th>
          </tr>
        </thead>

        <tbody className="bg-white">
          {services.map((service) => (
            <tr
              key={service._id}
              className="hover:bg-gray-50 transition-colors duration-150"
            >
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200 font-medium">
                {service.costServiceId}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200 font-medium">
                {service.name}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200 font-medium">
                {service.partnerInfo?.name ? (
                  <div className="flex items-center space-x-2">
                    <span>{service.partnerInfo.name}</span>
                    {service.partnerInfo.isDeleted && (
                      <span className="bg-red-100 text-red-800 text-xs font-semibold px-2 py-0.5 rounded">
                        {t("costservices:form.deleted")}!
                      </span>
                    )}
                  </div>
                ) : (
                  "-"
                )}
              </td>
              <td className="px-6 py-4 text-sm text-gray-500 border border-gray-200 max-w-[700px]">
                <p className="truncate" title={service.description || "-"}>
                  {service.description || "-"}
                </p>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                {service.type || "-"}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200 font-medium">
                €{service?.price || 0}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                {service.tax}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium border border-gray-200">
                <div className="flex items-center justify-end">
                  <ActionMenu
                    ariaLabel={t("common:labels.actions")}
                    items={
                      [
                        {
                          label: t("common:buttons.view"),
                          icon: <Eye className="w-4 h-4" />,
                          onClick: () => onEdit(service),
                        },
                        {
                          label: t("common:buttons.delete"),
                          icon: <Trash2 className="w-4 h-4" />,
                          onClick: () => onDelete(service._id),
                          variant: "danger",
                          divider: true,
                        },
                      ] as ActionMenuItem[]
                    }
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {services.length === 0 && !loading && (
        <div className="text-center py-12">
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t("costservices:table.noRecords")}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t("costservices:table.getStarted")}
          </p>
        </div>
      )}
    </div>
  );
};
